import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { instrumentSupabaseClient } from './perfLog';

/**
 * ALEX ERP — real Supabase client (Auth + PostgREST + RPC).
 *
 * STATUS: IMPLEMENTED_UNVERIFIED. Written against the real `@supabase/supabase-js`
 * v2 public API (createClient, auth.signInWithPassword, auth.signOut,
 * auth.getSession, auth.onAuthStateChange, .rpc, .from) exactly as it will run
 * once built by a CI environment with npm registry access. This build sandbox
 * cannot install `@supabase/supabase-js` itself (registry blocked — see
 * docs/data-dictionary.md §9, 2026-08-12/2026-08-15), so this file has been
 * syntax/bundle-checked (esbuild, externals stubbed) but never executed
 * against a real Supabase project from here. See docs/data-dictionary.md §8k
 * for the full WRITTEN-vs-EXECUTED evidence distinction.
 *
 * Auth session persistence (localStorage) and JWT refresh are handled
 * entirely by supabase-js itself — this project does not hand-roll token
 * storage or refresh logic, to avoid duplicating well-tested library code.
 *
 * Multi-tenancy note: tenant_id is NOT set here or anywhere in this file.
 * Per docs/data-dictionary.md §9 (2026-08-12 log), the real Supabase project
 * has a Custom Access Token (JWT) Claims Hook configured that injects the
 * authenticated user's tenant_id into every JWT automatically at sign-in —
 * every `.rpc()`/`.from()` call below is scoped by Postgres RLS
 * (app_current_tenant_id(), migration 0004) purely from that JWT claim. The
 * frontend never sends or asserts a tenant_id itself.
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // Fails loudly at build/boot time rather than silently hitting an empty
  // URL and producing confusing network errors later — matches this
  // project's "fail-closed, never guess" discipline (see migration 0032
  // header comment) applied to configuration, not just authorization.
  // eslint-disable-next-line no-console
  console.error(
    '[ALEX ERP] Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. Copy apps/web/.env.example to .env.local (dev) ' +
      'or set them in Cloudflare Pages -> Settings -> Environment variables (deployed build).'
  );
}

export const supabase: SupabaseClient = createClient(supabaseUrl ?? '', supabaseAnonKey ?? '', {
  auth: {
    // Real session restore: supabase-js persists the session to
    // localStorage and rehydrates it on page load by itself.
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// Real call-timing instrumentation (see lib/perfLog.ts) — exposes
// window.__alexPerf.summary()/.recent() in the browser console. Cheap
// (one Proxy + one timestamp pair per call) and never alters request
// behavior; left enabled in production too since it's the only way to
// capture real navigation latency against the real network path, which
// this build environment's outbound proxy cannot reach directly.
instrumentSupabaseClient(supabase);
